import { FormGroup } from '@angular/forms';
import { firstValueFrom, map } from 'rxjs';
import { tryParseToNumber } from '../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';

import { GeoCoordinateSystemService } from '../../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';
import { ULSService } from '../../../../../../core/services/uls/uls.service';
import { Injectable, inject } from '@angular/core';
import { ScheduleDStepTwoConstants } from '../../components/schedule-d-step-two/constants/schedule-d-step-two-constants';

@Injectable({
    providedIn: 'root',
})
export class PopulateScheduleDAsrDataService {
    private readonly ulsService = inject(ULSService);
    private readonly geoCoordinateSystemService = inject(
        GeoCoordinateSystemService,
    );

    private lastFetchedAsrNumber: number | null = null;

    clearLastFetchedAsrNumber(): void {
        this.lastFetchedAsrNumber = null;
    }

    async getAsrCoordinateData(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): Promise<void> {
        const asrNumber = tryParseToNumber(
            form.controls.towerRegistrationNumber.value,
        );
        if (asrNumber && asrNumber !== this.lastFetchedAsrNumber) {
            this.lastFetchedAsrNumber = asrNumber;
            try {
                await firstValueFrom(
                    this.ulsService.getASRLocation(asrNumber).pipe(
                        map((resp) => {
                            if (
                                resp &&
                                resp.latDegrees !== undefined &&
                                resp.longDegrees !== undefined
                            ) {
                                const asrLatitude =
                                    this.geoCoordinateSystemService.convertDMSToDecimal(
                                        {
                                            degrees: resp.latDegrees,
                                            minutes: resp.latMinutes,
                                            seconds: resp.latSeconds,
                                            direction: resp.latDirection,
                                        },
                                    );

                                const asrLongitude =
                                    this.geoCoordinateSystemService.convertDMSToDecimal(
                                        {
                                            degrees: resp.longDegrees,
                                            minutes: resp.longMinutes,
                                            seconds: resp.longSeconds,
                                            direction: resp.longDirection,
                                        },
                                    );

                                form.controls.latitude?.setValue(
                                    String(asrLatitude),
                                );
                                form.controls.longitude?.setValue(
                                    String(asrLongitude),
                                );

                                form.controls.streetAddress?.setValue(
                                    resp.locationAddress ?? null,
                                );
                                form.controls.city?.setValue(
                                    resp.locationCity ?? null,
                                );
                                form.controls.state?.setValue(
                                    resp.locationState ?? null,
                                );
                                form.controls.county?.setValue(
                                    resp.locationCounty ?? null,
                                );
                                form.controls.groundElevation?.setValue(
                                    resp.groundElevation !== undefined
                                        ? String(resp.groundElevation)
                                        : null,
                                );
                                form.controls.heightOfSupportStructure?.setValue(
                                    resp.heightOfSupportStructure !== undefined
                                        ? String(resp.heightOfSupportStructure)
                                        : null,
                                );
                                form.controls.overallHeightOfStructure?.setValue(
                                    resp.overallHeightOfStructure !== undefined
                                        ? String(resp.overallHeightOfStructure)
                                        : null,
                                );

                                // "TOWER" is a grandfathered structure type that must be changed for all new submissions
                                if (
                                    resp.structureType !==
                                    ScheduleDStepTwoConstants.StructureTypeTower
                                ) {
                                    form.controls.structureTypeCode?.setValue(
                                        resp.structureType ?? null,
                                    );
                                } else {
                                    form.controls.structureTypeCode?.markAsTouched();
                                }

                                return true;
                            }

                            return true;
                        }),
                    ),
                );
            } catch {
                // API call failure
                throw new Error(
                    'Failure to reach ULS API to retrieve ASR location data',
                );
            }
        }
    }
}
